import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LoaderCircle, Trash2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, get } from "@/lib/api";
import { qk } from "@/lib/queries";
import { useUi } from "@/lib/store";
import type { Workspace } from "@/lib/types";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { forgetDeletedNotes } from "@/features/notes/collab";

/** Permanent deletion of a notebook or project, confirmed by typing its name. */
export function DeleteWorkspaceDialog({ workspace, open, onOpenChange }: { workspace: Pick<Workspace, "id" | "name" | "kind" | "memberCount">; open: boolean; onOpenChange(o: boolean): void }) {
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const what = workspace.kind === "notebook" ? "notebook" : "project";
  const matches = typed.trim() === workspace.name.trim();
  const others = workspace.memberCount - 1;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (busy) return;
        if (!o) setTyped("");
        onOpenChange(o);
      }}
    >
      <DialogContent className="text-foreground sm:max-w-md">
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!matches) return;
            setBusy(true);
            try {
              const tree = await get<{ notes: { id: string }[] }>(`/api/workspaces/${workspace.id}/tree`).catch(() => ({ notes: [] }));
              const trash = await get<{ notes: { id: string }[] }>(`/api/workspaces/${workspace.id}/trash`).catch(() => ({ notes: [] }));
              await api(`/api/workspaces/${workspace.id}`, { method: "DELETE", body: { confirmName: typed } });
              const ui = useUi.getState();
              if (ui.userId) await forgetDeletedNotes(ui.userId, [...tree.notes, ...trash.notes].map((n) => n.id)).catch(() => undefined);
              ui.dropWorkspaceTabs(workspace.id);
              qc.removeQueries({ queryKey: qk.tree(workspace.id) });
              await qc.invalidateQueries({ queryKey: qk.workspaces });
              toast.success(`“${workspace.name}” was deleted.`);
              setTyped("");
              onOpenChange(false);
            } catch (err) {
              toast.error(errorMessage(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="size-4" aria-hidden /> Delete this {what}
            </DialogTitle>
            <DialogDescription>“{workspace.name}” and everything in it will be permanently deleted.</DialogDescription>
          </DialogHeader>
          <Alert variant="destructive">
            <TriangleAlert />
            <AlertTitle>This cannot be undone</AlertTitle>
            <AlertDescription>
              All notes with their version history and comments, folders, files, tasks{workspace.kind === "project" ? ", the library, manuscripts and their submission snapshots" : ""}, encryption keys
              and AI results are deleted from BrainLog.{" "}
              {others > 0 ? `${others} other member${others === 1 ? "" : "s"} will lose access immediately.` : ""} Full backups made earlier still contain it.
            </AlertDescription>
          </Alert>
          <div className="space-y-1.5">
            <Label htmlFor={`del-${workspace.id}`}>
              Type <strong className="font-semibold">{workspace.name}</strong> to confirm
            </Label>
            <Input id={`del-${workspace.id}`} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" autoFocus />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={!matches || busy}>
              {busy ? <LoaderCircle className="animate-spin" /> : <Trash2 />} Delete permanently
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
